import {
  EXTERIOR_CONDITIONS,
  EXTERIOR_CONDITION_LABELS,
  GARAGE_TYPES,
  GARAGE_TYPE_LABELS,
  GARDEN_TYPES,
  GARDEN_TYPE_LABELS,
  INTERIOR_CONDITIONS,
  INTERIOR_CONDITION_LABELS,
  OWNERSHIP_TYPES,
  OWNERSHIP_TYPE_LABELS,
  PROPERTY_KNOWN_ATTRIBUTE_KEYS,
  enumOptions,
  isPropertyKnownAttribute,
  type PropertyKnownAttribute,
} from '@aimmo/contracts';
import { formatScalar } from '../lib/format.js';

interface KnownAttribute {
  label: string;
  options: { value: string; label: string }[];
  labels: Record<string, string>;
}

/**
 * The attribute keys with a vocabulary (docs/domain/03-lookups-and-enums.md):
 * what the form asks as a select and the detail writes as a label.
 *
 * Keyed by the contract's own list, so a key added there without a label here
 * is a type error rather than a select with no name.
 */
export const KNOWN_ATTRIBUTES: Record<PropertyKnownAttribute, KnownAttribute> = {
  garden: {
    label: 'Giardino',
    options: enumOptions(GARDEN_TYPES, GARDEN_TYPE_LABELS),
    labels: GARDEN_TYPE_LABELS,
  },
  garage: {
    label: 'Box auto',
    options: enumOptions(GARAGE_TYPES, GARAGE_TYPE_LABELS),
    labels: GARAGE_TYPE_LABELS,
  },
  ownership: {
    label: 'Tipologia di proprietà',
    options: enumOptions(OWNERSHIP_TYPES, OWNERSHIP_TYPE_LABELS),
    labels: OWNERSHIP_TYPE_LABELS,
  },
  interiorCondition: {
    label: 'Stato interno',
    options: enumOptions(INTERIOR_CONDITIONS, INTERIOR_CONDITION_LABELS),
    labels: INTERIOR_CONDITION_LABELS,
  },
  exteriorCondition: {
    label: 'Stato esterno',
    options: enumOptions(EXTERIOR_CONDITIONS, EXTERIOR_CONDITION_LABELS),
    labels: EXTERIOR_CONDITION_LABELS,
  },
};

/** The kit's select fields for the known keys, bound inside the `attributes` bag. */
export const KNOWN_ATTRIBUTE_FIELDS = PROPERTY_KNOWN_ATTRIBUTE_KEYS.map((key) => ({
  name: `attributes.${key}`,
  label: KNOWN_ATTRIBUTES[key].label,
  type: 'select' as const,
  options: KNOWN_ATTRIBUTES[key].options,
}));

/** One entry of the bag as the detail shows it: a known key in words, any other as typed. */
export function formatAttribute(key: string, value: unknown): { label: string; value: string } {
  if (isPropertyKnownAttribute(key)) {
    const known = KNOWN_ATTRIBUTES[key];
    return {
      label: known.label,
      value: typeof value === 'string' ? (known.labels[value] ?? value) : formatScalar(value),
    };
  }
  return { label: key, value: formatScalar(value) };
}

/**
 * The bag in the order a reader wants it: the known keys first, in their own
 * order, then the long tail as it was typed. A known key nobody answered is
 * left out rather than shown as a dash.
 */
export function attributeEntries(bag: Record<string, unknown>): [string, unknown][] {
  const known = PROPERTY_KNOWN_ATTRIBUTE_KEYS.filter(
    (key) => bag[key] !== undefined && bag[key] !== null && bag[key] !== '',
  ).map((key): [string, unknown] => [key, bag[key]]);
  const free = Object.entries(bag).filter(([key]) => !isPropertyKnownAttribute(key));
  return [...known, ...free];
}
